import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { parentApi } from '../services/api';
import { ClassStatus, childClassStatus } from '../utils/classStatus';
import { useAuth } from './AuthContext';
import { setFunctional } from '../utils/cookieConsent';

/**
 * Parent → selected child context.
 *
 * Holds the parent's children list and which one is currently "in focus".
 * When the parent has only one child, that child auto-selects and
 * Performance / Assignments / appointments pages skip the selection modal.
 * When multiple children exist, pages guarded by <ChildRequiredGuard/> show
 * the selection modal until the parent picks one.
 *
 * Persisted to localStorage so navigating between pages keeps the context.
 */

export interface ChildSummary {
  id: string;            // student uuid
  name: string;
  surname: string;
  studentCode: string | null;
  className: string | null;
  gradeLevel: { id: number; number: number | null; name: string } | null;
  institution: { id: number; name: string } | null;
  // AP62: çocuğun profil fotoğrafı (imzalı URL) + bireysel bayrağı — veli UI'sı
  // kurum-bağımlı öğeleri (Öğrenci Ödevleri, Randevularım) buna göre gizler.
  avatarUrl: string | null;
  isIndividual: boolean;
  // The three states (utils/classStatus.ts) — 'awaiting' = kurumlu ama sınıfsız: the
  // veli sees Randevularım closed with the reason, and an explanation on Ödevler.
  classStatus: ClassStatus;
}

const STORAGE_KEY = 'dijipo_selected_child';

interface ChildContextValue {
  children: ChildSummary[];
  selectedChild: ChildSummary | null;
  loading: boolean;
  selectChild: (id: string) => void;
  clearSelection: () => void;
  refresh: () => Promise<void>;
}

const ChildContext = createContext<ChildContextValue | undefined>(undefined);

function readStored(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeStored(id: string | null) {
  try {
    if (id) setFunctional(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* quota ignored */ }
}

export const ChildProvider: React.FC<{ children: ReactNode }> = ({ children: content }) => {
  const { user } = useAuth();
  const [childrenList, setChildrenList] = useState<ChildSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(readStored());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (user?.role !== 'parent') return;
    setLoading(true);
    try {
      const { data } = await parentApi.dashboard();
      const kids: ChildSummary[] = (data?.children ?? []).map((c: any) => ({
        id: c.id,
        name: c.name ?? '',
        surname: c.surname ?? '',
        studentCode: c.studentCode ?? null,
        className: c.className ?? null,
        gradeLevel: c.gradeLevel ?? null,
        institution: c.institution ?? null,
        avatarUrl: c.avatarUrl ?? null,
        isIndividual: !!c.is_individual,
        classStatus: childClassStatus({ classStatus: c.class_status, isIndividual: c.is_individual }),
      }));
      setChildrenList(kids);
      // Auto-select when only one child — no modal needed.
      if (kids.length === 1) {
        setSelectedId(kids[0].id);
        writeStored(kids[0].id);
      } else if (selectedId && !kids.some(k => k.id === selectedId)) {
        // Stored ID no longer valid (child removed etc.) — reset.
        setSelectedId(null);
        writeStored(null);
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'parent') refresh();
    else {
      setChildrenList([]);
      setSelectedId(null);
    }
  }, [user?.role, refresh]);

  const selectChild = useCallback((id: string) => {
    setSelectedId(id);
    writeStored(id);
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedId(null);
    writeStored(null);
  }, []);

  const selectedChild = useMemo(
    () => childrenList.find(c => c.id === selectedId) ?? null,
    [childrenList, selectedId]
  );

  const value: ChildContextValue = {
    children: childrenList,
    selectedChild,
    loading,
    selectChild,
    clearSelection,
    refresh,
  };
  return <ChildContext.Provider value={value}>{content}</ChildContext.Provider>;
};

export function useChild(): ChildContextValue {
  const ctx = useContext(ChildContext);
  if (!ctx) throw new Error('useChild must be used inside ChildProvider');
  return ctx;
}
